import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import emplogin from "../Assets/Employer/EmpLogin.png";
import { FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";

function LoginForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState(''); // "success" or "error"
  const [isLoading, setIsLoading] = useState(false); // Loading state

  useEffect(() => {
    let timer;
    if (isOtpSent && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isOtpSent, resendTimer]);

  const handleSendOtp = async () => {
    if (!email) {
      setStatusMessage('Please enter a valid email address.');
      setStatusType('error');
      return;
    }

    try {
      setIsLoading(true); // Start loading
      const response = await axios.post('http://127.0.0.1:8000/employerLogin/send-otp1/', { email });
      setIsOtpSent(true);
      setResendTimer(30);
      setStatusMessage('OTP has been sent to your email.');
      setStatusType('success');
    } catch (error) {
      console.error('Error sending OTP:', error.response?.data || error.message);
      setStatusMessage('Failed to send OTP. Please try again.');
      setStatusType('error');
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleResendOtp = () => {
    if (resendTimer === 0) {
      handleSendOtp();
    }
  };

  const handleLogin = async () => {
    if (!otp || !password) {
      setStatusMessage('Please enter OTP and Password.');
      setStatusType('error');
      return;
    }

    try {
      setIsLoading(true); // Start loading
      const response = await axios.post('http://127.0.0.1:8000/employerLogin/verify-otp2/', { email, otp, password });
      setStatusMessage('Login successful!');
      setStatusType('success');
      setEmail('');
      setOtp('');
      setPassword('');
      navigate("/Employerdb");
    } catch (error) {
      console.error('Error verifying OTP:', error.response?.data || error.message);
      setStatusMessage('Invalid credentials. Please try again.');
      setStatusType('error');
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      <div className="flex w-full max-w-4xl bg-white rounded-lg shadow-md overflow-hidden">
        {/* Left Side - Image */}
        <div className="w-1/2 flex items-center justify-center bg-gray-50">
          <img
            src={emplogin}
            alt="Employee Login"
            className="w-3/4 h-auto object-contain"
          />
        </div>

        {/* Right Side - Form */}
        <div className="w-1/2 p-6">
          <div className="flex justify-between items-center">
            <span></span> {/* Empty span to create space on the left */}
            <button
              onClick={() => navigate("/")} // Navigate back
              className="flex items-center text-gray-300 hover:text-red-600 transition-colors duration-300"
            >
              <FaTimes className="mr-2" />
            </button>
          </div>
          <h2 className="text-2xl font-semibold text-center text-gray-700 mb-4">Login</h2>

          {/* Email Input */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your email"
            />
            {statusMessage && (
              <p
                className={`mt-2 text-sm ${statusType === 'success' ? 'text-green-600' : 'text-red-600'}`}
              >
                {statusMessage}
              </p>
            )}
          </div>

          {/* Verify Email Button */}
          <button
            onClick={handleSendOtp}
            className="w-full px-4 py-2 mb-4 text-white"
            style={{ backgroundColor: '#0077af' }}
            disabled={!email || isOtpSent || isLoading} // Disable button while loading
          >
            {isLoading && !isOtpSent ? 'Verifying...' : 'Verify Email'}
          </button>

          {isOtpSent && (
            <>
              {/* OTP Input */}
              <div className="mb-4">
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  OTP
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter OTP"
                />
              </div>

              {/* Password Input */}
              <div className="mb-4 relative">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {resendTimer > 0 ? (
                <p className="text-sm text-gray-500 mb-4">Resend OTP in {resendTimer} seconds</p>
              ) : (
                <button
                  onClick={handleResendOtp}
                  className="text-sm text-blue-500 hover:underline mb-4"
                >
                  Resend OTP
                </button>
              )}

              <button
                onClick={handleLogin}
                className="w-full px-4 py-2 text-white"
                style={{ backgroundColor: '#0077af' }}
                disabled={isLoading} // Disable button while loading
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginForm;
